import { Linking, Pressable } from 'react-native';
import { Text, XStack } from 'tamagui';
import type { PhotoAttribution } from '@my-fullstack-app/itinerary-types';
import { colors } from '../../theme/colors';

interface PhotoAttributionCaptionProps {
  attribution: PhotoAttribution | undefined | null;
  /** Visual style:
   *   - `'overlay'` — light text over a dark gradient hero (default).
   *   - `'inline'` — dim text on a surface (place card body).
   */
  variant?: 'overlay' | 'inline' | undefined;
}

/**
 * Photo author attribution per Google Maps Platform ToS §3.2.3.
 *
 * Required on every Place Photos surface — the photographer's name (and
 * link to their profile, when provided) must be visible alongside the
 * photo. Renders nothing when no attribution data exists so callers can
 * drop this in unconditionally (e.g. for legacy entries persisted
 * before attribution was captured at pick time).
 */
export function PhotoAttributionCaption({
  attribution,
  variant = 'overlay',
}: PhotoAttributionCaptionProps) {
  if (!attribution?.displayName) return null;
  const { displayName, uri } = attribution;

  const isOverlay = variant === 'overlay';
  const textColor = isOverlay ? 'rgba(255,255,255,0.78)' : '$textSubdued';

  const body = (
    <Text
      fontSize={10}
      color={textColor as string}
      numberOfLines={1}
      lineHeight={12}
    >
      Photo: {displayName}
    </Text>
  );

  return (
    <XStack
      paddingHorizontal={isOverlay ? 8 : 0}
      paddingVertical={isOverlay ? 4 : 2}
      borderRadius={isOverlay ? 6 : 0}
      backgroundColor={isOverlay ? colors.overlayScrim : undefined}
      alignSelf={isOverlay ? 'flex-start' : undefined}
    >
      {uri ? (
        <Pressable
          onPress={() => {
            void Linking.openURL(uri);
          }}
          accessibilityRole="link"
          accessibilityLabel={`Open photographer profile for ${displayName}`}
          hitSlop={12}
        >
          {body}
        </Pressable>
      ) : (
        body
      )}
    </XStack>
  );
}
